import React from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { IconButton, cn } from "../ui";
import TopicPlanNote from "./schedule/TopicPlanNote";
import { AttachmentActions, AttachmentChips } from "./SyllabusAttachments";

/**
 * TopicRow — one topic of a syllabus day on the trainer's Syllabus tab.
 *
 * One line: the tick, the name, and Map recording · Upload · Link existing.
 * The topic's recordings and materials follow on a second line only when it
 * has some. Ticked as covered only while the day is in progress; recordings
 * and materials can be added at any time, so a trainer can prepare a day
 * before teaching it, or file a recording of a class held outside the LMS.
 *
 * Props:
 *   index, topic, sessionStatus
 *   onToggle          — tick / untick as covered
 *   busy              — the day is saving something
 *   recordings: onMapRecording, onOpenRecording, onRemoveRecording
 *   materials, onUploadMaterial, onLinkMaterial, onOpenMaterial, onRemoveMaterial
 *   removingId        — the recording or material being removed right now
 */
const TopicRow = ({
  index, topic, sessionStatus, onToggle, busy, removingId,
  onMapRecording, onOpenRecording, onRemoveRecording,
  materials = [], onUploadMaterial, onLinkMaterial, onOpenMaterial, onRemoveMaterial,
}) => {
  const isCompleted = topic.status === "completed";
  const isToggleable = sessionStatus === "in_progress";
  const StateIcon = isCompleted ? CheckCircle2 : Circle;
  const stateTone = isCompleted ? "tw-text-success" : "tw-text-content-subtle";

  return (
    <li className="tw-flex tw-flex-col tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-line-soft tw-bg-surface tw-px-3 tw-py-1.5">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
        {isToggleable ? (
          <IconButton
            label={isCompleted ? `Mark “${topic.name}” as not covered` : `Mark “${topic.name}” as covered`}
            size="xs"
            aria-pressed={isCompleted}
            onClick={onToggle}
            disabled={busy}
          >
            <StateIcon size={15} aria-hidden="true" className={stateTone} />
          </IconButton>
        ) : (
          <span className="tw-inline-flex tw-shrink-0">
            <StateIcon size={15} aria-hidden="true" className={stateTone} />
            <span className="tw-sr-only">{isCompleted ? "Covered" : "Not covered yet"}</span>
          </span>
        )}
        <span className={cn("tw-min-w-[10rem] tw-flex-1 tw-text-[0.8125rem] tw-font-medium", isCompleted ? "tw-text-content-muted" : "tw-text-content")}>
          <span className="tw-text-content-subtle">{index + 1}.</span> {topic.name}
          <TopicPlanNote topic={topic} />
        </span>
        <AttachmentActions
          target={`“${topic.name}”`}
          onMapRecording={onMapRecording}
          onUpload={onUploadMaterial}
          onLink={onLinkMaterial}
        />
      </div>
      <AttachmentChips
        className="tw-pl-7"
        where="this topic"
        recordings={topic.recordings || []}
        materials={materials}
        removingId={removingId}
        onOpenRecording={onOpenRecording}
        onRemoveRecording={onRemoveRecording}
        onOpenMaterial={onOpenMaterial}
        onRemoveMaterial={onRemoveMaterial}
      />
    </li>
  );
};

export default TopicRow;
